<template>
  <div class="page page-content-wrapper">
    <div class="no-margin d-flex">
      <MInput v-model="filters.query" label="Advertiser name or ID:" placeholder="Asos" col="3" @enter="getStores" />
      <MSelect v-model="filters.language" label="Languages:" placeholder="All" col="2" :options="languages" />
      <MSelect v-model="filters.group" label="Group:" placeholder="All" col="2" :options="storeGroups" />
      <MSelect
        v-model="filters.provider"
        label="Provider:"
        placeholder="All"
        col="2"
        :options="[{ value: '', label: 'All' }, ...storeProviders.map((i) => ({ label: i.key, value: i.id }))]"
      />
      <MSelect
        v-model="filters.status"
        label="Status:"
        placeholder="All"
        col="2"
        :options="[
          { value: '', label: 'All' },
          { value: 0, label: 'Unset' },
          { value: 1, label: 'Approved' },
          { value: 2, label: 'Denied' },
        ]"
      />
      <MSelect
        v-model="filters.store_type"
        label="Type:"
        placeholder="All"
        col="2"
        :options="[
          { value: '', label: 'All' },
          { value: 'MANAGED', label: 'Managed' },
          { value: 'SELF_SERVICE', label: 'Self Service' },
        ]"
      />
      <el-button class="btn-icon" size="large" type="info" @click="getStores">
        <el-icon>
          <Search />
        </el-icon>
      </el-button>
      <el-button class="btn-icon" size="large" @click="exportStores">
        <el-icon>
          <Download />
        </el-icon>
      </el-button>
    </div>

    <div class="page-content">
      <div v-if="storesPerPage.length" class="no-margin">
        <div v-if="!loading" class="row">
          <MCard
            v-for="store in storesPerPage"
            :id="store.id"
            :key="store.id"
            col="4"
            :view-page="'/stores/' + store.id"
            :buttons="[
              {
                label: 'Delete',
                icon: 'Delete',
                callback: () => onDeleteClick(store),
              },
              {
                label: 'Statistic',
                icon: 'Histogram',
                callback: () => onStatisticClick(store),
              },
              {
                label: 'Edit',
                icon: 'Edit',
                callback: () => $router.push(`/stores/${store.id}/edit`),
              },
            ]"
          >
            <image-component
              v-if="store.logo || store.logo_url"
              :image="store.logo ? normalizeImage(store.logo) : { url: store.logo_url }"
              width="140"
              height="50"
              :alt="store.feed_name"
              class="store-logo"
            />
            <h1 v-else>{{ store.feed_name }}</h1>
            <div class="margin-top-small font-size-normal">
              Name: <span class="bold">{{ store.feed_name }}</span>
            </div>
            <div class="margin-top-none font-size-normal">
              ID: <span class="bold">{{ store.id }}</span>
            </div>
            <costs-data-row
              class="font-size-normal"
              :currency="store.currency_iso_code"
              :traffic-sources-costs="store.traffic_sources_costs"
            />
            <div class="margin-top-none font-size-normal">
              Provider: <span class="bold">{{ store.feed_provider }}</span>
            </div>
            <div class="margin-top-none font-size-normal">
              Store Group:
              <span class="bold">{{ getStoreGroupsList(store) }}</span>
            </div>
            <div class="margin-top-none font-size-normal">
              Store Type:
              <span class="bold">{{ store.store_type === 'MANAGED' ? 'Managed' : 'Self Service' }}</span>
            </div>
          </MCard>
        </div>
        <div v-else class="spinner-container">
          <div class="spinner" />
        </div>
      </div>
      <div v-else-if="!loading">No data</div>
    </div>

    <pagination :current-page="pagination.current" :last-page="pagination.last" :on-page-change="getStores" />
  </div>
</template>
<script>
import CostsDataRow from '@/components/CostsDataRow.vue'
import { config } from '@/lib/helpers/config.helpers'
import ImageComponent from '@/ui-library/atoms/ImageComponent.vue'
import { normalizeImage } from '@/ui-library/types/image.types'
import { Download, Search } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import { mapActions, mapGetters, mapState } from 'vuex'
import httpClient from '../../api/httpClient'
import MInput from '../../components/metapic/inputs/MInput.vue'
import MSelect from '../../components/metapic/inputs/MSelect.vue'
import MCard from '../../components/metapic/MCard.vue'
import Pagination from '../../components/metapic/page/Pagination.vue'
import { getCurrencySymbol } from '../../utils/helper'

export default {
  components: {
    ImageComponent,
    CostsDataRow,
    MInput,
    MSelect,
    Pagination,
    MCard,
    Search,
    Download,
  },
  computed: {
    ...mapState('stores/list', ['filters', 'languages', 'loading', 'pagination']),
    ...mapGetters('stores/list', ['storesPerPage', 'groups']),
    ...mapGetters('storeGroups', ['storeGroups']),
    ...mapGetters('stores', ['storeProviders']),
  },

  created() {
    if (!this.storeGroups.length) {
      this.fetch()
      this.setStoreProviders()
    }
    this.getStores()
  },
  methods: {
    normalizeImage,
    getCurrencySymbol,
    ...mapActions('stores/list', ['getStores', 'destroyStore']),
    ...mapActions('storeGroups', ['fetch']),
    ...mapActions('stores', ['setStoreProviders']),
    getStoreGroupsList(store) {
      return store.store_groups.map((group) => group.name).join(', ')
    },
    onDeleteClick(store) {
      ElMessageBox.confirm(`Are you sure you want to permanently delete: ${store.feed_name}?`)
        .then(() => {
          this.destroyStore({ store })
        })
        .catch((err) => console.log(err))
    },
    onStatisticClick(store) {
      window.open(`${config.advertiserApiUrl}/login?store_id=${store.id}`, '_blank')
    },
    async exportStores() {
      try {
        const endpoint = `${config.apiBaseUrl}/admin/stores/?format=xlsx&query=${this.filters.query}&status=${this.filters.status}&group=${this.filters.group}&language=${this.filters.language}`

        const response = await httpClient.get(endpoint, {
          responseType: 'blob',
        })

        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', 'stores.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)
      } catch (error) {
        if (error && error.isAxiosError) {
          const axiosError = error
          if (axiosError.response && axiosError.response.data instanceof Blob) {
            try {
              const text = await axiosError.response.data.text()
              const json = JSON.parse(text)
              await ElMessageBox.alert(json?.message || 'Something went wrong, please try again later.', 'Export failed', { type: 'error' })
            } catch (e) {
              await ElMessageBox.alert('Something went wrong, please try again later.', 'Export failed', { type: 'error' })
            }
            return
          }
        }

        console.error(error)
      }
    },
  },
}
</script>
<style scoped>
.row {
  margin-left: -8px;
  margin-right: -8px;
  display: flex;
  flex-wrap: wrap;
}
.store-logo {
  height: 50px;
  max-width: 140px;
  margin: 0;
  width: fit-content;
  object-fit: contain;
}
.font-size-normal {
  font-size: 22px;
  line-height: 40px;
}
.margin-top-small {
  margin-top: 10px;
}
.btn-icon {
  padding: 24px;
  aspect-ratio: 1;
}
.d-flex {
  display: flex;
}
</style>
